import { PageShell } from "@superadmin/components/PageShell";
import { RateFormDialog } from "@superadmin/components/RateForm";
import {
	describePricing,
	fromMicros,
	istToday,
	scopeLabel,
	toMicros,
} from "@superadmin/utils/cost";
import { formatEpoch } from "@superadmin/utils/format";
import { orpc } from "@superadmin/utils/orpc.tanstack.client";
import { type EditIntent, editParamZod, resolveRateEditing } from "@superadmin/utils/pricing_edit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LoadingSpinner } from "@voicely/ui-mui/components/LoadingSpinner";
import { Chip } from "@voicely/ui-mui/data-display/Chip";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@voicely/ui-mui/data-display/Table";
import { Tooltip } from "@voicely/ui-mui/data-display/Tooltip";
import { Typography } from "@voicely/ui-mui/data-display/Typography";
import { Alert } from "@voicely/ui-mui/feedback/Alert";
import { AlertTitle } from "@voicely/ui-mui/feedback/AlertTitle";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@voicely/ui-mui/feedback/Dialog";
import { Button } from "@voicely/ui-mui/form/Button";
import { MenuItem } from "@voicely/ui-mui/form/MenuItem";
import { Switch } from "@voicely/ui-mui/form/Switch";
import { TextField } from "@voicely/ui-mui/form/TextField";
import { ToggleButton } from "@voicely/ui-mui/form/ToggleButton";
import { ToggleButtonGroup } from "@voicely/ui-mui/form/ToggleButtonGroup";
import { Paper } from "@voicely/ui-mui/layout/Paper";
import { Stack } from "@voicely/ui-mui/layout/Stack";
import { useUrlState } from "@voicely/web-shared/use_url_state";
import {
	COST_SERVICES,
	COST_UNITS,
	type CostBinding,
	type CreateVersionInput,
	FEE_BASES,
	type FixedFee,
	type FxRate,
	POST_CALL_UNITS,
	type PostCallBinding,
	type Rate,
	type RateInput,
	type RetireVersionInput,
	type Slab,
} from "@voicely/zod-schemas/costing.zod";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { toast } from "react-toastify";
import { z } from "zod";

const tabSchema = z.enum(["rates", "slabs", "fees", "fx"]);
const flagSchema = z.enum(["0", "1"]);
/** The "new …" dialog on the slabs, fees and fx tabs. `?dialog=`, absent when closed. */
const newDialogSchema = z.enum(["new-slab", "new-fee", "new-fx"]).nullable();

const addDaysIso = (date: string, days: number) =>
	new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);

const validity = (from: string, to: string | null) => (to ? `${from} → ${to}` : `${from} onwards`);

/**
 * The rate book: what every unit of vendor usage costs, when, and why.
 *
 * ## Versions, not edits
 *
 * Nothing on this page edits a number in place. "Replace" writes a new version
 * and closes the old one; a new period for a scope that already has an
 * open-ended rate closes that rate at the new start date; "Retire" closes one
 * with no successor. Every figure the calculator has ever shown can therefore
 * be reproduced — it names the version that priced it, and the history toggle
 * shows every version there has been.
 *
 * ## A non-zero rate must say where it came from
 *
 * **A rate nobody can trace reads exactly like one that was checked**, and a
 * cost calculator built on plausible invented numbers is worse than one with
 * visible gaps. The save is refused without a source.
 *
 * ## The worklist is at the top
 *
 * Bindings real traffic hit in the last 30 days that nothing prices. That is
 * where a new vendor names itself, on its first call, rather than on an invoice.
 * Each one opens the rate form already scoped to it.
 *
 * ## Post-call models are listed before they have traffic
 *
 * The worklist can only name a binding after a call was costed without it.
 * Post-call summary and extraction are the leg where that is too late — an
 * extraction model is one dropdown away in the agent builder, and on a short
 * call the two post-call requests can cost more than the conversation — so the
 * models agents are configured with are listed from configuration, with each
 * unit's standing today.
 */
export const PricingPage = () => {
	const queryClient = useQueryClient();
	const [tab, setTab] = useUrlState("tab", tabSchema, "rates");
	const [history, setHistory] = useUrlState("history", flagSchema, "0");

	const book = useQuery(orpc.superAdmin.rateBook.queryOptions());

	const worklistWindow = useMemo(() => {
		const to = istToday();
		return { from: addDaysIso(to, -30), to };
	}, []);

	const worklist = useQuery(
		orpc.superAdmin.costCalculator.queryOptions({
			input: {
				filters: { ...worklistWindow, teamIds: [], agentIds: [], outcome: "all" },
				groupBy: "none",
				draftRates: [],
				callsLimit: 1,
				callsOffset: 0,
			},
		}),
	);

	const coverage = useQuery(orpc.superAdmin.postCallCoverage.queryOptions());

	// One dialog for the page: the rates tab, the worklist and the post-call
	// panel all open it. Which one, and on what, is URL state (`?edit=`); the
	// worklist's prefill is unsaved input and stays in memory — see
	// `pricing_edit.ts` for what that means on a reload.
	const [edit, setEdit] = useUrlState<EditIntent>("edit", editParamZod, null);
	const [prefill, setPrefill] = useState<Partial<RateInput> | undefined>(undefined);
	const editing = resolveRateEditing(edit, book.data?.rates ?? [], prefill);
	const openEditor = (intent: EditIntent, initial?: Partial<RateInput>) => {
		setPrefill(initial);
		setEdit(intent);
	};
	const closeEditor = () => {
		setPrefill(undefined);
		setEdit(null);
	};

	const refresh = () => queryClient.invalidateQueries();

	const create = useMutation(
		orpc.costing.createVersion.mutationOptions({
			onSuccess: () => {
				toast.success("Saved as a new version. Figures for its period reflect it immediately.");
				refresh();
			},
			onError: (error) => toast.error(error.message),
		}),
	);

	const retire = useMutation(
		orpc.costing.retireVersion.mutationOptions({
			onSuccess: () => {
				toast.success(
					"Retired. Its traffic falls to the next rate up the hierarchy, or to unpriced.",
				);
				refresh();
			},
			onError: (error) => toast.error(error.message),
		}),
	);

	const save = (input: CreateVersionInput, done: () => void) =>
		create.mutate(input, { onSuccess: done });

	/**
	 * A worklist binding as a rate scope. Purpose is left on "any" unless the
	 * line was post-call: a conversation-only rate is the rare case, and a
	 * model priced for everything is what most unpriced keys want.
	 */
	const addForBinding = (binding: CostBinding) =>
		openEditor("unpriced", {
			service: binding.service,
			gateway: binding.gateway,
			vendor: binding.vendor,
			model: binding.model,
			purpose: binding.purpose === "post_call" ? "post_call" : null,
			unit: binding.unit as RateInput["unit"],
		});

	const close = (input: RetireVersionInput) => {
		if (window.confirm("Retire this version? It stays on the record; it stops pricing anything.")) {
			retire.mutate(input);
		}
	};

	const toolbar = (
		<Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap", gap: 1 }}>
			<ToggleButtonGroup
				exclusive
				size="small"
				value={tab}
				onChange={(_e, next) => next && setTab(next)}
			>
				<ToggleButton value="rates">Rates</ToggleButton>
				<ToggleButton value="slabs">Volume slabs</ToggleButton>
				<ToggleButton value="fees">Fixed fees</ToggleButton>
				<ToggleButton value="fx">Currency</ToggleButton>
			</ToggleButtonGroup>
			<Stack direction="row" alignItems="center">
				<Switch
					checked={history === "1"}
					onChange={(e) => setHistory(e.target.checked ? "1" : "0")}
				/>
				<Typography variant="body2">Show superseded versions</Typography>
			</Stack>
			<Link to="/costing">Open the calculator</Link>
		</Stack>
	);

	if (book.error) {
		return (
			<PageShell title="Rate book" toolbar={toolbar}>
				<Alert severity="error">{book.error.message}</Alert>
			</PageShell>
		);
	}

	if (!book.data) {
		return (
			<PageShell title="Rate book" toolbar={toolbar}>
				<LoadingSpinner />
			</PageShell>
		);
	}

	const showAll = history === "1";
	const gaps = worklist.data?.gaps;

	return (
		<PageShell
			title="Rate book"
			subtitle="Dated, versioned rates at service → gateway → vendor → model level"
			toolbar={toolbar}
			maxWidth={false}
		>
			<Stack spacing={3}>
				{gaps && gaps.unpricedKeys.length > 0 && (
					<Alert severity="warning">
						<AlertTitle>Traffic in the last 30 days that no rate prices</AlertTitle>
						<Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>
							{gaps.unpricedKeys.map((key, index) => {
								const binding = gaps.unpriced[index];

								return (
									<Chip
										key={key}
										label={key}
										size="small"
										variant="outlined"
										onClick={binding ? () => addForBinding(binding) : undefined}
									/>
								);
							})}
						</Stack>
					</Alert>
				)}
				{gaps && gaps.unconvertedCurrencies.length > 0 && (
					<Alert severity="warning">
						No conversion rate for {gaps.unconvertedCurrencies.join(", ")} on some days in the last
						30 — those lines are left out of every total.
					</Alert>
				)}

				{tab === "rates" && (
					<PostCallPanel
						coverage={coverage.data}
						error={coverage.error?.message ?? null}
						onAdd={(binding, unit) =>
							openEditor("postcall", {
								service: "llm",
								gateway: binding.gateway,
								vendor: binding.vendor,
								model: binding.model,
								purpose: "post_call",
								unit,
							})
						}
					/>
				)}
				{tab === "rates" && (
					<RatesTab
						rates={book.data.rates}
						showAll={showAll}
						onEdit={openEditor}
						onRetire={(id) => close({ kind: "rate", id })}
					/>
				)}
				{tab === "slabs" && (
					<SlabsTab
						slabs={book.data.slabs}
						showAll={showAll}
						pending={create.isPending}
						onSave={(slab, done) => save({ kind: "slab", slab }, done)}
						onRetire={(id) => close({ kind: "slab", id })}
					/>
				)}
				{tab === "fees" && (
					<FeesTab
						fees={book.data.fixedFees}
						showAll={showAll}
						pending={create.isPending}
						onSave={(fee, done) => save({ kind: "fixed_fee", fee }, done)}
						onRetire={(id) => close({ kind: "fixed_fee", id })}
					/>
				)}
				{tab === "fx" && (
					<FxTab
						fx={book.data.fxRates}
						showAll={showAll}
						pending={create.isPending}
						onSave={(fx, done) => save({ kind: "fx_rate", fx }, done)}
						onRetire={(id) => close({ kind: "fx_rate", id })}
					/>
				)}
			</Stack>
			<RateFormDialog
				open={editing !== null}
				title={editing?.title ?? ""}
				initial={editing?.initial}
				submitLabel="Save version"
				pending={create.isPending}
				onClose={closeEditor}
				onSubmit={(rate) => save({ kind: "rate", rate }, closeEditor)}
			/>
		</PageShell>
	);
};

/**
 * The models post-call summary and extraction run on, and which of their
 * units the book prices today.
 *
 * A cell resolves exactly as a real `post_call` line dated today would, so a
 * purpose-less rate shows here as pricing it — it does. "shared" says so, and
 * the caption says how to price post-call apart instead.
 */
function PostCallPanel({
	coverage,
	error,
	onAdd,
}: {
	coverage: PostCallBinding[] | undefined;
	error: string | null;
	onAdd: (binding: PostCallBinding, unit: (typeof POST_CALL_UNITS)[number]) => void;
}) {
	if (error) return <Alert severity="error">Post-call models could not be read: {error}</Alert>;
	if (!coverage) return null;

	const missing = coverage.filter(
		(binding) => binding.agents > 0 && binding.units.some((u) => u.level === null),
	).length;

	return (
		<TableContainer component={Paper} variant="outlined">
			<Stack sx={{ p: 1.5, pb: 0 }} spacing={0.5}>
				<Typography variant="subtitle2">Post-call analysis models</Typography>
				<Typography variant="body2" color="text.secondary">
					After every call with a caller turn, the agent's own LLM writes the summary and the model
					chosen under Extractions fills the fields — one request each, over the whole transcript.
					Listed from agent configuration, so a model is priceable before its first call. “Add”
					scopes the rate to post-call; set Purpose to “Any” instead to share the conversation's
					rate.
				</Typography>
				{missing > 0 && (
					<Typography variant="body2" color="warning.main">
						{missing} model(s) in use have a unit nothing prices — their post-call cost is counted
						as unknown, not free.
					</Typography>
				)}
			</Stack>
			{coverage.length === 0 ? (
				<Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
					No agent is configured with an inference model.
				</Typography>
			) : (
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Model</TableCell>
							<TableCell>Used for</TableCell>
							<TableCell align="right">Agents</TableCell>
							{POST_CALL_UNITS.map((unit) => (
								<TableCell key={unit}>{unit}</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{coverage.map((binding) => (
							<TableRow key={`${binding.provider}/${binding.model}`}>
								<TableCell>
									{scopeLabel({
										service: "llm",
										gateway: binding.gateway,
										vendor: binding.vendor,
										model: binding.model,
									})}
								</TableCell>
								<TableCell>
									{binding.roles.length === 0 ? (
										<Typography variant="caption" color="text.secondary">
											offered, not picked yet
										</Typography>
									) : (
										<Stack direction="row" spacing={0.5}>
											{binding.roles.map((role) => (
												<Chip key={role} label={role} size="small" />
											))}
										</Stack>
									)}
								</TableCell>
								<TableCell align="right">{binding.agents}</TableCell>
								{binding.units.map((cell) => (
									<TableCell key={cell.unit} sx={{ whiteSpace: "nowrap" }}>
										{cell.level === null ? (
											<Button
												size="small"
												color="warning"
												onClick={() => onAdd(binding, cell.unit)}
											>
												No rate — add
											</Button>
										) : (
											<Tooltip
												title={
													cell.postCallOnly
														? "Priced by a post-call rate"
														: "Priced by a rate shared with the conversation"
												}
											>
												<Chip
													label={`${cell.level} · ${cell.postCallOnly ? "post-call" : "shared"}`}
													size="small"
													variant="outlined"
													color="success"
												/>
											</Tooltip>
										)}
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}
		</TableContainer>
	);
}

const StatusChip = ({ supersededAt }: { supersededAt: number | null }) =>
	supersededAt === null ? (
		<Chip label="in force" size="small" color="success" variant="outlined" />
	) : (
		<Tooltip title={`Superseded ${formatEpoch(supersededAt)}`}>
			<Chip label="superseded" size="small" />
		</Tooltip>
	);

function RatesTab({
	rates,
	showAll,
	onEdit,
	onRetire,
}: {
	rates: Rate[];
	showAll: boolean;
	onEdit: (intent: EditIntent) => void;
	onRetire: (id: string) => void;
}) {
	const visible = rates
		.filter((rate) => showAll || rate.supersededAt === null)
		.sort((a, b) =>
			`${a.service}${scopeLabel(a)}${a.unit}${a.validFrom}`.localeCompare(
				`${b.service}${scopeLabel(b)}${b.unit}${b.validFrom}`,
			),
		);

	return (
		<>
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Typography variant="body2" color="text.secondary">
					The most specific in-force rate valid on a call's date prices it. A level left blank
					inherits from above.
				</Typography>
				<Button variant="contained" onClick={() => onEdit("new")}>
					New rate
				</Button>
			</Stack>
			{COST_SERVICES.map((service) => {
				const rows = visible.filter((rate) => rate.service === service);

				return (
					<TableContainer key={service} component={Paper} variant="outlined">
						<Typography variant="subtitle2" sx={{ p: 1.5, pb: 0 }}>
							{service}
						</Typography>
						{rows.length === 0 ? (
							<Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
								No rates — every {service} line is unpriced.
							</Typography>
						) : (
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Level</TableCell>
										<TableCell>Scope</TableCell>
										<TableCell>Unit</TableCell>
										<TableCell>Pricing</TableCell>
										<TableCell>Valid</TableCell>
										<TableCell>Source</TableCell>
										<TableCell>Status</TableCell>
										<TableCell align="right" />
									</TableRow>
								</TableHead>
								<TableBody>
									{rows.map((rate) => (
										<TableRow key={rate.id} sx={{ opacity: rate.supersededAt === null ? 1 : 0.55 }}>
											<TableCell>
												<Chip label={rate.level} size="small" variant="outlined" />
											</TableCell>
											<TableCell>
												{scopeLabel(rate)}
												{rate.purpose && ` [${rate.purpose}]`}
											</TableCell>
											<TableCell>{rate.unit}</TableCell>
											<TableCell>{describePricing(rate.pricing, rate.currency)}</TableCell>
											<TableCell sx={{ whiteSpace: "nowrap" }}>
												{validity(rate.validFrom, rate.validTo)}
											</TableCell>
											<TableCell>
												<Tooltip
													title={[rate.note, rate.createdByEmail && `by ${rate.createdByEmail}`]
														.filter(Boolean)
														.join(" — ")}
												>
													<Typography variant="caption">{rate.source ?? "—"}</Typography>
												</Tooltip>
											</TableCell>
											<TableCell>
												<StatusChip supersededAt={rate.supersededAt} />
											</TableCell>
											<TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
												{rate.supersededAt === null && (
													<>
														<Button size="small" onClick={() => onEdit(`replace:${rate.id}`)}>
															Replace
														</Button>
														<Button size="small" onClick={() => onEdit(`period:${rate.id}`)}>
															New period
														</Button>
														<Button size="small" color="error" onClick={() => onRetire(rate.id)}>
															Retire
														</Button>
													</>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						)}
					</TableContainer>
				);
			})}
		</>
	);
}

function SlabsTab({
	slabs,
	showAll,
	pending,
	onSave,
	onRetire,
}: {
	slabs: Slab[];
	showAll: boolean;
	pending: boolean;
	onSave: (slab: Extract<CreateVersionInput, { kind: "slab" }>["slab"], done: () => void) => void;
	onRetire: (id: string) => void;
}) {
	const [dialog, setDialog] = useUrlState<"new-slab" | null>(
		"dialog",
		newDialogSchema.pipe(z.literal("new-slab").nullable()),
		null,
	);
	const open = dialog === "new-slab";
	const setOpen = (next: boolean) => setDialog(next ? "new-slab" : null);
	const [form, setForm] = useState({
		service: "telephony",
		vendor: "vobiz",
		unit: "talk_seconds",
		currency: "INR",
		tiers: "100000:0.5/60\n*:0.4/60",
		validFrom: istToday(),
		source: "",
	});
	const [problem, setProblem] = useState<string | null>(null);

	const submit = () => {
		const tiers = form.tiers
			.split("\n")
			.map((line) => line.trim())
			.filter(Boolean)
			.map((line) => {
				const match = line.match(/^(\*|\d+(?:\.\d+)?)\s*:\s*([\d.]+)\s*\/\s*(\d+(?:\.\d+)?)$/);
				const price = match ? toMicros(match[2] ?? "") : null;

				return match && price !== null
					? {
							upTo: match[1] === "*" ? null : Number(match[1]),
							priceMicros: price,
							per: Number(match[3]),
						}
					: null;
			});

		if (tiers.length === 0 || tiers.some((tier) => tier === null)) {
			setProblem("Each tier is one line, `upTo:price/per`, with `*` as the last tier's upTo.");
			return;
		}

		setProblem(null);
		onSave(
			{
				service: form.service as (typeof COST_SERVICES)[number],
				gateway: null,
				vendor: form.vendor.trim() || null,
				model: null,
				unit: form.unit as (typeof COST_UNITS)[number],
				tiers: tiers as Array<{ upTo: number | null; priceMicros: number; per: number }>,
				currency: form.currency.toUpperCase(),
				validFrom: form.validFrom,
				validTo: null,
				source: form.source || undefined,
			},
			() => setOpen(false),
		);
	};

	return (
		<>
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Typography variant="body2" color="text.secondary">
					A vendor's price by monthly volume. Shown beside the calculator's total as an adjustment
					from list price, computed on the whole platform's month — never inside a call.
				</Typography>
				<Button variant="contained" onClick={() => setOpen(true)}>
					New slab
				</Button>
			</Stack>
			<TableContainer component={Paper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Scope</TableCell>
							<TableCell>Unit</TableCell>
							<TableCell>Tiers</TableCell>
							<TableCell>Valid</TableCell>
							<TableCell>Status</TableCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{slabs
							.filter((slab) => showAll || slab.supersededAt === null)
							.map((slab) => (
								<TableRow key={slab.id}>
									<TableCell>{scopeLabel(slab)}</TableCell>
									<TableCell>{slab.unit}</TableCell>
									<TableCell>
										{slab.tiers
											.map(
												(tier) =>
													`${tier.upTo === null ? "above" : `≤${tier.upTo}`}: ${fromMicros(tier.priceMicros)}/${tier.per}`,
											)
											.join("; ")}{" "}
										{slab.currency}
									</TableCell>
									<TableCell>{validity(slab.validFrom, slab.validTo)}</TableCell>
									<TableCell>
										<StatusChip supersededAt={slab.supersededAt} />
									</TableCell>
									<TableCell align="right">
										{slab.supersededAt === null && (
											<Button size="small" color="error" onClick={() => onRetire(slab.id)}>
												Retire
											</Button>
										)}
									</TableCell>
								</TableRow>
							))}
					</TableBody>
				</Table>
			</TableContainer>
			<Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>New volume slab</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ pt: 1 }}>
						<Stack direction="row" spacing={1.5}>
							<TextField
								select
								size="small"
								label="Service"
								value={form.service}
								onChange={(e) => setForm({ ...form, service: e.target.value })}
							>
								{COST_SERVICES.map((s) => (
									<MenuItem key={s} value={s}>
										{s}
									</MenuItem>
								))}
							</TextField>
							<TextField
								size="small"
								label="Vendor"
								value={form.vendor}
								onChange={(e) => setForm({ ...form, vendor: e.target.value })}
							/>
							<TextField
								select
								size="small"
								label="Unit"
								value={form.unit}
								onChange={(e) => setForm({ ...form, unit: e.target.value })}
							>
								{COST_UNITS.map((u) => (
									<MenuItem key={u} value={u}>
										{u}
									</MenuItem>
								))}
							</TextField>
							<TextField
								size="small"
								label="Currency"
								value={form.currency}
								onChange={(e) => setForm({ ...form, currency: e.target.value })}
								sx={{ width: 100 }}
							/>
						</Stack>
						<TextField
							multiline
							minRows={3}
							size="small"
							label="Tiers: cumulative monthly upTo:price/per"
							value={form.tiers}
							onChange={(e) => setForm({ ...form, tiers: e.target.value })}
							helperText="First 100,000 seconds at 0.5 per minute, then 0.4: 100000:0.5/60 and *:0.4/60"
						/>
						<TextField
							size="small"
							type="date"
							label="Valid from"
							value={form.validFrom}
							onChange={(e) => setForm({ ...form, validFrom: e.target.value })}
							slotProps={{ inputLabel: { shrink: true } }}
						/>
						<TextField
							size="small"
							label="Source"
							value={form.source}
							onChange={(e) => setForm({ ...form, source: e.target.value })}
						/>
						{problem && <Alert severity="error">{problem}</Alert>}
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpen(false)}>Cancel</Button>
					<Button variant="contained" disabled={pending} onClick={submit}>
						Save version
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

function FeesTab({
	fees,
	showAll,
	pending,
	onSave,
	onRetire,
}: {
	fees: FixedFee[];
	showAll: boolean;
	pending: boolean;
	onSave: (
		fee: Extract<CreateVersionInput, { kind: "fixed_fee" }>["fee"],
		done: () => void,
	) => void;
	onRetire: (id: string) => void;
}) {
	const [dialog, setDialog] = useUrlState<"new-fee" | null>(
		"dialog",
		newDialogSchema.pipe(z.literal("new-fee").nullable()),
		null,
	);
	const open = dialog === "new-fee";
	const setOpen = (next: boolean) => setDialog(next ? "new-fee" : null);
	const [form, setForm] = useState({
		service: "telephony",
		vendor: "vobiz",
		description: "Number rental",
		basis: "per_phone_number",
		amount: "",
		currency: "INR",
		validFrom: istToday(),
		source: "",
	});

	const submit = () => {
		const amount = toMicros(form.amount);

		if (amount === null) {
			toast.error("The amount must be a number with at most six decimals.");
			return;
		}

		onSave(
			{
				service: form.service as (typeof COST_SERVICES)[number],
				vendor: form.vendor.trim() || null,
				description: form.description,
				basis: form.basis as (typeof FEE_BASES)[number],
				amountMicros: amount,
				currency: form.currency.toUpperCase(),
				validFrom: form.validFrom,
				validTo: null,
				source: form.source || undefined,
			},
			() => setOpen(false),
		);
	};

	return (
		<>
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Typography variant="body2" color="text.secondary">
					Monthly charges no call causes. Prorated by days in the calculator, beside the total.
				</Typography>
				<Button variant="contained" onClick={() => setOpen(true)}>
					New fixed fee
				</Button>
			</Stack>
			<TableContainer component={Paper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>What</TableCell>
							<TableCell>Service / vendor</TableCell>
							<TableCell>Per month</TableCell>
							<TableCell>Valid</TableCell>
							<TableCell>Status</TableCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{fees
							.filter((fee) => showAll || fee.supersededAt === null)
							.map((fee) => (
								<TableRow key={fee.id}>
									<TableCell>{fee.description}</TableCell>
									<TableCell>
										{fee.service}
										{fee.vendor && ` › ${fee.vendor}`}
									</TableCell>
									<TableCell>
										{fromMicros(fee.amountMicros)} {fee.currency}
										{fee.basis !== "flat" && ` ${fee.basis.replace("_", " ")}`}
									</TableCell>
									<TableCell>{validity(fee.validFrom, fee.validTo)}</TableCell>
									<TableCell>
										<StatusChip supersededAt={fee.supersededAt} />
									</TableCell>
									<TableCell align="right">
										{fee.supersededAt === null && (
											<Button size="small" color="error" onClick={() => onRetire(fee.id)}>
												Retire
											</Button>
										)}
									</TableCell>
								</TableRow>
							))}
					</TableBody>
				</Table>
			</TableContainer>
			<Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>New fixed fee</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ pt: 1 }}>
						<TextField
							size="small"
							label="Description"
							value={form.description}
							onChange={(e) => setForm({ ...form, description: e.target.value })}
						/>
						<Stack direction="row" spacing={1.5}>
							<TextField
								select
								size="small"
								label="Service"
								value={form.service}
								onChange={(e) => setForm({ ...form, service: e.target.value })}
							>
								{COST_SERVICES.map((s) => (
									<MenuItem key={s} value={s}>
										{s}
									</MenuItem>
								))}
							</TextField>
							<TextField
								size="small"
								label="Vendor"
								value={form.vendor}
								onChange={(e) => setForm({ ...form, vendor: e.target.value })}
							/>
							<TextField
								select
								size="small"
								label="Per"
								value={form.basis}
								onChange={(e) => setForm({ ...form, basis: e.target.value })}
							>
								{FEE_BASES.map((basis) => (
									<MenuItem key={basis} value={basis}>
										{basis}
									</MenuItem>
								))}
							</TextField>
						</Stack>
						<Stack direction="row" spacing={1.5}>
							<TextField
								size="small"
								label="Amount per month"
								value={form.amount}
								onChange={(e) => setForm({ ...form, amount: e.target.value })}
							/>
							<TextField
								size="small"
								label="Currency"
								value={form.currency}
								onChange={(e) => setForm({ ...form, currency: e.target.value })}
								sx={{ width: 100 }}
							/>
							<TextField
								size="small"
								type="date"
								label="Valid from"
								value={form.validFrom}
								onChange={(e) => setForm({ ...form, validFrom: e.target.value })}
								slotProps={{ inputLabel: { shrink: true } }}
							/>
						</Stack>
						<TextField
							size="small"
							label="Source"
							value={form.source}
							onChange={(e) => setForm({ ...form, source: e.target.value })}
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpen(false)}>Cancel</Button>
					<Button variant="contained" disabled={pending} onClick={submit}>
						Save version
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

function FxTab({
	fx,
	showAll,
	pending,
	onSave,
	onRetire,
}: {
	fx: FxRate[];
	showAll: boolean;
	pending: boolean;
	onSave: (fx: Extract<CreateVersionInput, { kind: "fx_rate" }>["fx"], done: () => void) => void;
	onRetire: (id: string) => void;
}) {
	const [dialog, setDialog] = useUrlState<"new-fx" | null>(
		"dialog",
		newDialogSchema.pipe(z.literal("new-fx").nullable()),
		null,
	);
	const open = dialog === "new-fx";
	const setOpen = (next: boolean) => setDialog(next ? "new-fx" : null);
	const [form, setForm] = useState({
		currency: "USD",
		rate: "",
		markup: "0",
		validFrom: istToday(),
		source: "",
	});

	const submit = () => {
		const rate = toMicros(form.rate);
		const markupBps = Math.round(Number(form.markup) * 100);

		if (rate === null || rate === 0 || !Number.isFinite(markupBps)) {
			toast.error(
				"The rate must be a positive number with at most six decimals, and the markup a percentage.",
			);
			return;
		}

		onSave(
			{
				currency: form.currency.toUpperCase(),
				microsPerUnit: rate,
				markupBps,
				validFrom: form.validFrom,
				validTo: null,
				source: form.source || undefined,
			},
			() => setOpen(false),
		);
	};

	return (
		<>
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Typography variant="body2" color="text.secondary">
					What one unit of a vendor's currency costs in INR, per period. A line converts at the rate
					valid on its call's date; the markup is what the bank or card adds.
				</Typography>
				<Button variant="contained" onClick={() => setOpen(true)}>
					New rate
				</Button>
			</Stack>
			<TableContainer component={Paper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Currency</TableCell>
							<TableCell>INR per unit</TableCell>
							<TableCell>Markup</TableCell>
							<TableCell>Valid</TableCell>
							<TableCell>Source</TableCell>
							<TableCell>Status</TableCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{fx
							.filter((row) => showAll || row.supersededAt === null)
							.map((row) => (
								<TableRow key={row.id}>
									<TableCell>{row.currency}</TableCell>
									<TableCell>{fromMicros(row.microsPerUnit)}</TableCell>
									<TableCell>{row.markupBps / 100}%</TableCell>
									<TableCell>{validity(row.validFrom, row.validTo)}</TableCell>
									<TableCell>{row.source ?? "—"}</TableCell>
									<TableCell>
										<StatusChip supersededAt={row.supersededAt} />
									</TableCell>
									<TableCell align="right">
										{row.supersededAt === null && (
											<Button size="small" color="error" onClick={() => onRetire(row.id)}>
												Retire
											</Button>
										)}
									</TableCell>
								</TableRow>
							))}
					</TableBody>
				</Table>
			</TableContainer>
			<Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>New conversion rate</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ pt: 1 }}>
						<Typography variant="body2" color="text.secondary">
							Starting a new period for a currency that has an open-ended rate closes that rate at
							this date; the earlier period keeps its own rate.
						</Typography>
						<Stack direction="row" spacing={1.5}>
							<TextField
								size="small"
								label="Currency"
								value={form.currency}
								onChange={(e) => setForm({ ...form, currency: e.target.value })}
								sx={{ width: 100 }}
							/>
							<TextField
								size="small"
								label="INR per unit"
								value={form.rate}
								onChange={(e) => setForm({ ...form, rate: e.target.value })}
								placeholder="88.50"
							/>
							<TextField
								size="small"
								label="Markup %"
								value={form.markup}
								onChange={(e) => setForm({ ...form, markup: e.target.value })}
								sx={{ width: 110 }}
							/>
						</Stack>
						<TextField
							size="small"
							type="date"
							label="Valid from"
							value={form.validFrom}
							onChange={(e) => setForm({ ...form, validFrom: e.target.value })}
							slotProps={{ inputLabel: { shrink: true } }}
						/>
						<TextField
							size="small"
							label="Source"
							value={form.source}
							onChange={(e) => setForm({ ...form, source: e.target.value })}
							helperText="A card statement, an RBI reference rate, a vendor invoice's own conversion."
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpen(false)}>Cancel</Button>
					<Button variant="contained" disabled={pending} onClick={submit}>
						Save version
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
